import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Avatar, Chip, Grid, Divider,
  InputAdornment, Collapse, CircularProgress, Snackbar, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton,
} from "@mui/material";
import { SideBarWidth } from "../../utils/SideBarWidth";
import { Colors } from "../../Theme/Theme";
import { hub } from "../../api/requests/hubs/hubs";
import { UserTreeApi } from "../../api/requests/network/network";
import SearchIcon from "@mui/icons-material/Search";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PhoneIcon from "@mui/icons-material/Phone";
import GroupIcon from "@mui/icons-material/Group";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BarChartIcon from "@mui/icons-material/BarChart";

const AdminUserNetwork = () => {
  const [users, setUsers] = useState([]);
  const [networkData, setNetworkData] = useState([]);
  const [search, setSearch] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [walletRes, networkRes] = await Promise.all([
        hub.getUsersWallet(),
        UserTreeApi.getUserNetwork().catch(() => ({ data: [] })),
      ]);
      setUsers(walletRes?.users || []);
      setNetworkData(networkRes?.data || []);
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to load user data", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const userLookup = {};
  users.forEach((u) => {
    if (u.user?.shortcode) userLookup[u.user.shortcode] = u;
  });

  const buildReferralMap = (nodes, parent, map) => {
    if (!nodes || !Array.isArray(nodes)) return;
    nodes.forEach((node) => {
      const code = node.attributes?.shortcode || node.shortcode || "";
      if (code) {
        const parentUser = parent ? userLookup[parent.attributes?.shortcode || parent.shortcode || ""] : null;
        map[code] = {
          referralName: parent?.name || "Root",
          referralCode: parent?.attributes?.shortcode || parent?.shortcode || "---",
          referralPhone: parentUser?.user?.phone || parentUser?.user?.mobileNumber || "N/A",
        };
      }
      if (node.children?.length > 0) {
        buildReferralMap(node.children, node, map);
      }
    });
  };

  const buildChildrenMap = (nodes, map) => {
    if (!nodes || !Array.isArray(nodes)) return;
    nodes.forEach((node) => {
      const code = node.attributes?.shortcode || node.shortcode || "";
      if (code && node.children) {
        map[code] = node.children.map((c) => ({
          name: c.name,
          shortcode: c.attributes?.shortcode || c.shortcode || "---",
          childrenCount: countAllMembers(c.children),
        }));
      }
      if (node.children?.length > 0) buildChildrenMap(node.children, map);
    });
  };

  const countAllMembers = (nodes) => {
    if (!nodes || !Array.isArray(nodes)) return 0;
    return nodes.reduce((sum, c) => sum + 1 + countAllMembers(c.children), 0);
  };

  const buildTeamSizeMap = (nodes, map) => {
    if (!nodes || !Array.isArray(nodes)) return;
    nodes.forEach((node) => {
      const code = node.attributes?.shortcode || node.shortcode || "";
      if (code) map[code] = countAllMembers(node.children);
      if (node.children?.length > 0) buildTeamSizeMap(node.children, map);
    });
  };

  const referralMap = {};
  buildReferralMap(networkData, null, referralMap);

  const childrenMap = {};
  buildChildrenMap(networkData, childrenMap);

  const teamSizeMap = {};
  buildTeamSizeMap(networkData, teamSizeMap);

  const calcBusiness = (walletHistory) => {
    if (!walletHistory || walletHistory.length === 0) return { credit: 0, withdrawal: 0, balance: 0 };
    let credit = 0, withdrawal = 0;
    walletHistory.forEach((t) => {
      const amt = parseFloat(t.amount) || 0;
      if (t.type?.toLowerCase() === "credit") credit += amt;
      else if (t.type?.toLowerCase() === "withdrawal" || t.type?.toLowerCase() === "debit") withdrawal += amt;
    });
    return { credit, withdrawal, balance: credit - withdrawal };
  };

  const calcPeriodBusiness = (walletHistory) => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const yearStart = new Date(now.getFullYear(), 0, 1);

    let daily = 0, monthly = 0, yearly = 0;
    (walletHistory || []).forEach((t) => {
      if (t.type?.toLowerCase() !== "credit") return;
      const amt = parseFloat(t.amount) || 0;
      const ts = new Date(t.timestamp);
      if (ts >= todayStart) daily += amt;
      if (ts >= monthStart) monthly += amt;
      if (ts >= yearStart) yearly += amt;
    });
    return { daily, monthly, yearly };
  };

  const calcTeamBusiness = (shortcode) => {
    const directMembers = childrenMap[shortcode] || [];
    let teamTotal = 0;
    const collectTeamBiz = (code) => {
      const u = userLookup[code];
      if (u) teamTotal += calcBusiness(u.walletHistory).credit;
      const children = childrenMap[code] || [];
      children.forEach((c) => collectTeamBiz(c.shortcode));
    };
    directMembers.forEach((c) => collectTeamBiz(c.shortcode));
    return teamTotal;
  };

  const filteredUsers = users.filter((item) => {
    const q = search.toLowerCase();
    return (
      item.user?.name?.toLowerCase().includes(q) ||
      item.user?.emailId?.toLowerCase().includes(q) ||
      item.user?.shortcode?.toLowerCase().includes(q) ||
      item.user?.phone?.toLowerCase().includes(q)
    );
  });

  const totalBusiness = users.reduce((sum, u) => sum + calcBusiness(u.walletHistory).credit, 0);

  const periodTotals = users.reduce((acc, u) => {
    const p = calcPeriodBusiness(u.walletHistory);
    return { daily: acc.daily + p.daily, monthly: acc.monthly + p.monthly, yearly: acc.yearly + p.yearly };
  }, { daily: 0, monthly: 0, yearly: 0 });

  const stats = [
    { label: "Total Users", value: users.length, icon: <PeopleAltIcon sx={{ fontSize: 20 }} />, bg: Colors.blueSoft, color: Colors.primary },
    { label: "Today's Business", value: `Rs. ${periodTotals.daily.toLocaleString("en-IN")}`, icon: <CalendarTodayIcon sx={{ fontSize: 20 }} />, bg: Colors.greenSoft, color: "#059669" },
    { label: "This Month", value: `Rs. ${periodTotals.monthly.toLocaleString("en-IN")}`, icon: <TrendingUpIcon sx={{ fontSize: 20 }} />, bg: "#FFFBEB", color: "#F59E0B" },
    { label: "This Year", value: `Rs. ${periodTotals.yearly.toLocaleString("en-IN")}`, icon: <BarChartIcon sx={{ fontSize: 20 }} />, bg: "#F5F3FF", color: "#8B5CF6" },
    { label: "All Time", value: `Rs. ${totalBusiness.toLocaleString("en-IN")}`, icon: <AccountBalanceWalletIcon sx={{ fontSize: 20 }} />, bg: Colors.greenSoft, color: "#059669" },
    { label: "Network Size", value: Object.values(teamSizeMap).reduce((a, b) => a + b, 0), icon: <AccountTreeIcon sx={{ fontSize: 20 }} />, bg: Colors.blueSoft, color: Colors.primary },
  ];

  const getReferralChain = (shortcode) => {
    const chain = [];
    let current = shortcode;
    const visited = new Set();
    while (current && referralMap[current] && !visited.has(current)) {
      visited.add(current);
      const ref = referralMap[current];
      chain.unshift({ name: ref.referralName, code: ref.referralCode, phone: ref.referralPhone });
      current = ref.referralCode;
    }
    return chain;
  };

  const maxBar = Math.max(periodTotals.daily, periodTotals.monthly, periodTotals.yearly, 1);

  return (
    <Box sx={{
      width: { xs: "100%", md: `calc(100% - ${SideBarWidth}px)` },
      ml: { md: `${SideBarWidth}px` },
      minHeight: "100vh", backgroundColor: Colors.bgColor,
    }}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <AccountTreeIcon sx={{ fontSize: 22, color: Colors.primary }} />
            <Typography sx={{ fontWeight: 500, color: Colors.primaryTextColor, fontSize: { xs: "20px", md: "24px" }, letterSpacing: "-0.02em" }}>
              User Network Explorer
            </Typography>
          </Box>
          <Typography sx={{ color: Colors.secondaryColor, fontSize: "13px", fontWeight: 400 }}>
            Detailed user information, referral chains, team structure, and business volume.
          </Typography>
        </Box>

        {/* Stats */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {stats.map((s, i) => (
            <Grid item xs={6} sm={4} md={2} key={i}>
              <Box sx={{
                p: 2, borderRadius: "12px", backgroundColor: Colors.white,
                border: `1px solid ${Colors.dividerColor}`,
              }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography sx={{ fontSize: "10px", fontWeight: 500, color: Colors.secondaryColor, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    {s.label}
                  </Typography>
                  <Avatar sx={{ width: 28, height: 28, backgroundColor: s.bg, color: s.color }}>
                    {s.icon}
                  </Avatar>
                </Box>
                <Typography sx={{ fontWeight: 400, fontSize: "18px", color: "#059669" }}>
                  {s.value}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Business Graph */}
        <Box sx={{
          backgroundColor: Colors.white, borderRadius: "12px",
          border: `1px solid ${Colors.dividerColor}`, p: 2.5, mb: 3,
        }}>
          <Typography sx={{ fontSize: "13px", fontWeight: 500, color: Colors.secondaryColor, textTransform: "uppercase", letterSpacing: "0.5px", mb: 2 }}>
            Business Overview
          </Typography>
          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
            {[
              { label: "Today", value: periodTotals.daily, color: "#059669" },
              { label: "This Month", value: periodTotals.monthly, color: "#2563EB" },
              { label: "This Year", value: periodTotals.yearly, color: "#8B5CF6" },
            ].map((bar, i) => (
              <Box key={i} sx={{ flex: "1 1 120px", minWidth: 100 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                  <Typography sx={{ fontSize: "12px", fontWeight: 400, color: Colors.secondaryColor }}>{bar.label}</Typography>
                  <Typography sx={{ fontSize: "12px", fontWeight: 400, color: "#059669" }}>Rs. {bar.value.toLocaleString("en-IN")}</Typography>
                </Box>
                <Box sx={{ height: 8, backgroundColor: "#F1F5F9", borderRadius: "4px", overflow: "hidden" }}>
                  <Box sx={{
                    height: "100%", borderRadius: "4px",
                    backgroundColor: bar.color,
                    width: `${maxBar > 0 ? (bar.value / maxBar) * 100 : 0}%`,
                    transition: "width 0.6s ease",
                  }} />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Search */}
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth placeholder="Search by name, email, shortcode or phone..."
            value={search} onChange={(e) => setSearch(e.target.value)} size="small"
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: Colors.secondaryColor, fontSize: 20 }} /></InputAdornment>,
            }}
            sx={{
              maxWidth: 480,
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px", backgroundColor: Colors.white,
                "& fieldset": { borderColor: Colors.dividerColor },
                "&:hover fieldset": { borderColor: Colors.primary },
                "&.Mui-focused fieldset": { borderColor: Colors.primary, borderWidth: "2px" },
              },
            }}
          />
        </Box>

        {/* Table */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={36} sx={{ color: Colors.primary }} />
          </Box>
        ) : (
          <Box sx={{ backgroundColor: Colors.white, borderRadius: "12px", border: `1px solid ${Colors.dividerColor}`, overflow: "hidden" }}>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    {["", "User", "Phone", "Shortcode", "Level", "Referral", "Team", "Balance", "Business", "Joined"].map((h) => (
                      <TableCell key={h} sx={{
                        fontWeight: 500, fontSize: "11px", color: Colors.secondaryColor,
                        textTransform: "uppercase", letterSpacing: "0.5px", py: 1.5,
                        backgroundColor: "#F9FBFC", borderBottom: `1px solid ${Colors.dividerColor}`,
                        whiteSpace: "nowrap",
                      }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} sx={{ textAlign: "center", py: 6 }}>
                        <Typography sx={{ color: Colors.secondaryColor, fontSize: "14px", fontWeight: 400 }}>
                          {search ? "No users match your search" : "No users found"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers.map((item) => {
                      const user = item.user;
                      const shortcode = user?.shortcode || "---";
                      const ref = referralMap[shortcode];
                      const teamSize = teamSizeMap[shortcode] || 0;
                      const biz = calcBusiness(item.walletHistory);
                      const isExpanded = expandedRow === user?.id;
                      const chain = getReferralChain(shortcode);
                      const directChildren = childrenMap[shortcode] || [];
                      const period = calcPeriodBusiness(item.walletHistory);
                      const teamBiz = calcTeamBusiness(shortcode);

                      return (
                        <React.Fragment key={user?.id}>
                          <TableRow
                            onClick={() => setExpandedRow(isExpanded ? null : user?.id)}
                            sx={{ cursor: "pointer", "&:hover": { backgroundColor: "#FAFBFF" } }}
                          >
                            <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}`, width: 36, px: 1 }}>
                              <IconButton size="small">
                                {isExpanded ? <KeyboardArrowUpIcon sx={{ fontSize: 18 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}` }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <Avatar sx={{ width: 30, height: 30, backgroundColor: Colors.blueSoft, color: Colors.primary, fontSize: 13, fontWeight: 500 }}>
                                  {(user?.name || "U").charAt(0).toUpperCase()}
                                </Avatar>
                                <Box>
                                  <Typography sx={{ fontSize: "13px", fontWeight: 500, color: Colors.primaryTextColor, lineHeight: 1.3 }}>
                                    {user?.name || "N/A"}
                                  </Typography>
                                  <Typography sx={{ fontSize: "10px", fontWeight: 400, color: Colors.secondaryColor }}>
                                    {user?.emailId || ""}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}` }}>
                              <Typography sx={{ fontSize: "12px", fontWeight: 400, color: Colors.primaryTextColor }}>
                                {user?.phone || user?.mobileNumber || "N/A"}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}` }}>
                              <Chip label={shortcode} size="small" sx={{
                                height: 20, fontSize: "10px", fontWeight: 500,
                                backgroundColor: Colors.blueSoft, color: Colors.primary,
                                "& .MuiChip-label": { px: 0.6 },
                              }} />
                            </TableCell>
                            <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}` }}>
                              <Typography sx={{ fontSize: "12px", fontWeight: 400, color: "#059669" }}>{item.level || "---"}</Typography>
                            </TableCell>
                            <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}` }}>
                              <Typography sx={{ fontSize: "12px", fontWeight: 400, color: Colors.primaryTextColor }}>
                                {ref ? ref.referralName : "---"}
                              </Typography>
                              {ref && (
                                <>
                                  <Typography sx={{ fontSize: "10px", fontWeight: 400, color: Colors.secondaryColor }}>
                                    ID: {ref.referralCode}
                                  </Typography>
                                  <Typography sx={{ fontSize: "10px", fontWeight: 400, color: Colors.secondaryColor }}>
                                    Ph: {ref.referralPhone}
                                  </Typography>
                                </>
                              )}
                            </TableCell>
                            <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}` }}>
                              <Typography sx={{ fontSize: "12px", fontWeight: 400, color: "#059669" }}>{teamSize}</Typography>
                            </TableCell>
                            <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}` }}>
                              <Typography sx={{ fontSize: "12px", fontWeight: 400, color: "#059669" }}>
                                Rs. {biz.balance.toLocaleString("en-IN")}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}` }}>
                              <Typography sx={{ fontSize: "12px", fontWeight: 400, color: "#059669" }}>
                                Rs. {biz.credit.toLocaleString("en-IN")}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}`, whiteSpace: "nowrap" }}>
                              <Typography sx={{ fontSize: "11px", fontWeight: 400, color: Colors.secondaryColor }}>
                                {user?.createdAt ? new Date(user.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "---"}
                              </Typography>
                            </TableCell>
                          </TableRow>

                          {/* Expanded */}
                          <TableRow>
                            <TableCell colSpan={10} sx={{ p: 0, borderBottom: isExpanded ? `1px solid ${Colors.dividerColor}` : "none" }}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ p: 3, backgroundColor: "#FAFBFF" }}>
                                  <Grid container spacing={2}>
                                    {/* Profile */}
                                    <Grid item xs={12} md={3}>
                                      <Box sx={{ backgroundColor: Colors.white, borderRadius: "12px", border: `1px solid ${Colors.dividerColor}`, p: 2, height: "100%" }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                                          <Avatar sx={{ width: 40, height: 40, backgroundColor: Colors.blueSoft, color: Colors.primary, fontSize: 16, fontWeight: 500 }}>
                                            {(user?.name || "U").charAt(0).toUpperCase()}
                                          </Avatar>
                                          <Box>
                                            <Typography sx={{ fontSize: "14px", fontWeight: 500, color: Colors.primaryTextColor }}>{user?.name || "N/A"}</Typography>
                                            <Typography sx={{ fontSize: "11px", fontWeight: 400, color: Colors.secondaryColor }}>{user?.emailId}</Typography>
                                          </Box>
                                        </Box>
                                        <Divider sx={{ borderColor: Colors.dividerColor, mb: 1.5 }} />
                                        <InfoRow label="Shortcode" value={shortcode} />
                                        <InfoRow label="Phone" value={user?.phone || user?.mobileNumber || "N/A"} />
                                        <InfoRow label="Level" value={item.level || "N/A"} />
                                        <InfoRow label="Hub" value={item.hubName || "N/A"} />
                                        <InfoRow label="KYC" value={user?.kyc_status || "PENDING"} />
                                        <InfoRow label="Joined" value={user?.createdAt ? new Date(user.createdAt).toLocaleDateString("en-IN") : "N/A"} isLast />
                                      </Box>
                                    </Grid>

                                    {/* Business breakdown */}
                                    <Grid item xs={12} md={3}>
                                      <Box sx={{ backgroundColor: Colors.white, borderRadius: "12px", border: `1px solid ${Colors.dividerColor}`, p: 2, height: "100%" }}>
                                        <Typography sx={{ fontSize: "11px", fontWeight: 500, color: Colors.secondaryColor, textTransform: "uppercase", letterSpacing: "0.5px", mb: 1.5 }}>
                                          Business Breakdown
                                        </Typography>
                                        <InfoRow label="Personal Earnings" value={`Rs. ${biz.credit.toLocaleString("en-IN")}`} />
                                        <InfoRow label="Team Business" value={`Rs. ${teamBiz.toLocaleString("en-IN")}`} />
                                        <InfoRow label="Total Withdrawn" value={`Rs. ${biz.withdrawal.toLocaleString("en-IN")}`} valueColor="#EF4444" />
                                        <InfoRow label="Wallet Balance" value={`Rs. ${biz.balance.toLocaleString("en-IN")}`} />
                                        <Divider sx={{ borderColor: "#F1F5F9", my: 1 }} />
                                        <InfoRow label="Today" value={`Rs. ${period.daily.toLocaleString("en-IN")}`} />
                                        <InfoRow label="This Month" value={`Rs. ${period.monthly.toLocaleString("en-IN")}`} />
                                        <InfoRow label="This Year" value={`Rs. ${period.yearly.toLocaleString("en-IN")}`} isLast />
                                      </Box>
                                    </Grid>

                                    {/* Referral Chain */}
                                    <Grid item xs={12} md={3}>
                                      <Box sx={{ backgroundColor: Colors.white, borderRadius: "12px", border: `1px solid ${Colors.dividerColor}`, p: 2, height: "100%" }}>
                                        <Typography sx={{ fontSize: "11px", fontWeight: 500, color: Colors.secondaryColor, textTransform: "uppercase", letterSpacing: "0.5px", mb: 1.5 }}>
                                          Referral Chain
                                        </Typography>
                                        {chain.length === 0 ? (
                                          <Typography sx={{ fontSize: "12px", fontWeight: 400, color: Colors.secondaryColor }}>Root level user</Typography>
                                        ) : (
                                          <Box>
                                            {chain.map((node, idx) => {
                                              const refUser = userLookup[node.code];
                                              const refBiz = refUser ? calcBusiness(refUser.walletHistory).credit : 0;
                                              return (
                                                <Box key={idx}>
                                                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, py: 0.8 }}>
                                                    <Avatar sx={{ width: 24, height: 24, backgroundColor: Colors.blueSoft, color: Colors.primary, fontSize: 11, fontWeight: 400, mt: 0.2 }}>
                                                      {(node.name || "?").charAt(0).toUpperCase()}
                                                    </Avatar>
                                                    <Box sx={{ flex: 1 }}>
                                                      <Typography sx={{ fontSize: "12px", fontWeight: 400, color: Colors.primaryTextColor }}>{node.name}</Typography>
                                                      <Typography sx={{ fontSize: "10px", fontWeight: 400, color: Colors.secondaryColor }}>
                                                        ID: {node.code} | Ph: {node.phone}
                                                      </Typography>
                                                      <Typography sx={{ fontSize: "10px", fontWeight: 400, color: "#059669" }}>
                                                        Business: Rs. {refBiz.toLocaleString("en-IN")}
                                                      </Typography>
                                                    </Box>
                                                  </Box>
                                                  {idx < chain.length - 1 && (
                                                    <Box sx={{ pl: 1.5 }}>
                                                      <Box sx={{ width: 1, height: 12, backgroundColor: Colors.dividerColor, ml: "3px" }} />
                                                    </Box>
                                                  )}
                                                </Box>
                                              );
                                            })}
                                            <Box sx={{ pl: 1.5, mb: 0.5 }}>
                                              <Box sx={{ width: 1, height: 12, backgroundColor: Colors.primary, ml: "3px" }} />
                                            </Box>
                                            <Box sx={{
                                              display: "flex", alignItems: "center", gap: 1, py: 0.8,
                                              px: 1, borderRadius: "6px", backgroundColor: Colors.blueSoft,
                                              border: `1px solid ${Colors.blueBorder}`,
                                            }}>
                                              <Avatar sx={{ width: 24, height: 24, backgroundColor: Colors.primary, color: Colors.white, fontSize: 11, fontWeight: 400 }}>
                                                {(user?.name || "?").charAt(0).toUpperCase()}
                                              </Avatar>
                                              <Box>
                                                <Typography sx={{ fontSize: "12px", fontWeight: 500, color: Colors.primary }}>{user?.name} (current)</Typography>
                                                <Typography sx={{ fontSize: "10px", fontWeight: 400, color: Colors.secondaryColor }}>
                                                  ID: {shortcode} | Ph: {user?.phone || "N/A"}
                                                </Typography>
                                              </Box>
                                            </Box>
                                          </Box>
                                        )}
                                      </Box>
                                    </Grid>

                                    {/* Direct Team Members */}
                                    <Grid item xs={12} md={3}>
                                      <Box sx={{ backgroundColor: Colors.white, borderRadius: "12px", border: `1px solid ${Colors.dividerColor}`, p: 2, height: "100%" }}>
                                        <Typography sx={{ fontSize: "11px", fontWeight: 500, color: Colors.secondaryColor, textTransform: "uppercase", letterSpacing: "0.5px", mb: 1.5 }}>
                                          Direct Team ({directChildren.length})
                                        </Typography>
                                        {directChildren.length === 0 ? (
                                          <Typography sx={{ fontSize: "12px", fontWeight: 400, color: Colors.secondaryColor }}>No direct members</Typography>
                                        ) : (
                                          <Box sx={{ maxHeight: 280, overflowY: "auto" }}>
                                            {directChildren.map((child, idx) => {
                                              const childUser = userLookup[child.shortcode];
                                              const childBiz = childUser ? calcBusiness(childUser.walletHistory).credit : 0;
                                              const childTeam = teamSizeMap[child.shortcode] || 0;
                                              return (
                                                <Box key={idx} sx={{
                                                  py: 1, borderBottom: idx < directChildren.length - 1 ? "1px solid #F1F5F9" : "none",
                                                }}>
                                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.3 }}>
                                                    <Avatar sx={{ width: 22, height: 22, backgroundColor: Colors.blueSoft, color: Colors.primary, fontSize: 10, fontWeight: 400 }}>
                                                      {(child.name || "?").charAt(0).toUpperCase()}
                                                    </Avatar>
                                                    <Typography sx={{ fontSize: "12px", fontWeight: 400, color: Colors.primaryTextColor }}>{child.name}</Typography>
                                                  </Box>
                                                  <Box sx={{ pl: 3.5 }}>
                                                    <Typography sx={{ fontSize: "10px", fontWeight: 400, color: Colors.secondaryColor }}>
                                                      ID: {child.shortcode} | Ph: {childUser?.user?.phone || "N/A"}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: "10px", fontWeight: 400, color: "#059669" }}>
                                                      Business: Rs. {childBiz.toLocaleString("en-IN")} | Team: {childTeam}
                                                    </Typography>
                                                  </Box>
                                                </Box>
                                              );
                                            })}
                                          </Box>
                                        )}
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: "10px" }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

const InfoRow = ({ label, value, valueColor, isLast }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.6, borderBottom: isLast ? "none" : "1px solid #F1F5F9" }}>
    <Typography sx={{ fontSize: "11px", fontWeight: 400, color: "#64748B" }}>{label}</Typography>
    <Typography sx={{ fontSize: "11px", fontWeight: 400, color: valueColor || "#059669" }}>{value}</Typography>
  </Box>
);

export default AdminUserNetwork;
